// Tiered scoring teams. Pure, deterministic — no imports outside ./handicap and domain
// types, matching the rest of engine/*.
//
// A SCORING TEAM IS NOT A GROUP. `teams` (supabase/migrations/0001) is the WALKING GROUP
// that tees off together and shares a card — a "group" in the app's own new copy. A scoring
// team is a tiered unit drafted from handicap so a strong player and a weak player are
// always on the same team, and its members can be scattered across different groups and
// different tee times. Two separate concepts, two separate ids on `entrants`
// (`team_id` for the group, `scoring_team_id` for this).
//
// DRAFTING (`draftTiers`). One player per tier per team, snake draft, so team strength comes
// out balanced without anyone doing the arithmetic by hand — the same mechanism a fantasy
// draft uses to keep the third pick from being worse off than the first. A host can always
// move somebody afterward; this only proposes a starting point.
//
// SCORING (`scoringTeamBoard`). best1/best2/aggregate are just "how many of a team's
// simultaneous scores on a hole count, and how many" — one low ball, two low balls, or
// everybody's. Basis (gross/net) decides what a member's own number is before the team rule
// ever sees it. This is the shared engine so the host's console and the phone can never
// disagree about who is winning a scoring-team pool, the same reason `leaderboard()` in
// src/sync/leaderboard.ts is shared for the group board.

import { allocateStrokes } from './handicap';

export type TeamRule = 'best1' | 'best2' | 'aggregate';
export type TeamBasis = 'gross' | 'net';

/**
 * separate (default): scoring teams are their own drafted pool, cutting across walking
 * groups — the whole point of the rest of this file. same: the walking group (what `teams` in
 * supabase/migrations/0001 already is) IS the scoring team — a host running a scramble, a
 * shotgun member-guest, or any event where the field already knows who is on whose team picks
 * this. See supabase/migrations/0022_team_mode.sql for the column and reseat_groups(), and
 * src/sync/leaderboard.ts's scoringTeamRowsFromPayload for how a payload is read differently
 * under each mode.
 */
export type TeamMode = 'separate' | 'same';

export type DraftPlayer = { id: string; handicapIndex: number | null };

export type DraftedTeam = { seq: number; name: string; memberIds: string[] };

export type DraftResult = {
  teams: DraftedTeam[];
  /** entrant id → tier, 1 = A tier (lowest handicaps). */
  tierOf: Record<string, number>;
  /** Entrants drafted at scratch (0) because they had no handicap index on file. */
  unranked: string[];
};

/** "1 → A", "2 → B", ... Presentational only; the stored value is always the number. */
export function tierLetter(tier: number | null | undefined): string {
  if (tier == null || tier < 1) return '';
  return String.fromCharCode('A'.charCodeAt(0) + tier - 1);
}

/**
 * Draft `players` into `teamCount` teams of `teamSize` members, one per tier, by snake draft
 * on handicap index (low index — the better player — goes first). Shared by `draftTiers` and
 * `draftGroups`, which differ only in what the resulting units are called — a SEPARATE-mode
 * scoring team or a SAME-mode walking group, respectively (see `TeamMode`). The balancing math
 * is identical either way: a host reseating the whole field into foursomes from handicap wants
 * exactly the same fairness a scoring-team draft does.
 *
 * T = ceil(n / teamSize) teams. Tier t (1..teamSize) is the players ranked
 * (t-1)*T+1 .. t*T — exactly enough for one per team. Odd tiers fill teams 1..T, even tiers
 * fill T..1, so tier 2's best player lands on the team that got tier 1's worst, and so on —
 * the standard snake-draft balancing trick. A field that does not divide evenly simply
 * leaves the last tier short; those teams end up with fewer than `teamSize` members, which a
 * host sees and can fix by hand.
 *
 * A null handicap index is treated as scratch (0) for ordering purposes — it sits wherever a
 * 0 would fall — and the entrant id is also returned in `unranked` so the console can flag it
 * rather than silently draft a real GHIN index and a guess side by side as equals.
 *
 * Deterministic: ties in handicap break by id, so re-running the draft on the same field
 * (before any host edit) always produces the same teams.
 */
function snakeDraft(players: DraftPlayer[], teamSize: number, namePrefix: string): DraftResult {
  const unranked = players.filter((p) => p.handicapIndex == null).map((p) => p.id);
  const size = Math.max(1, Math.floor(teamSize) || 1);
  const n = players.length;
  const teamCount = n === 0 ? 0 : Math.ceil(n / size);

  const teams: DraftedTeam[] = Array.from({ length: teamCount }, (_, i) => ({
    seq: i + 1,
    name: `${namePrefix} ${i + 1}`,
    memberIds: [],
  }));
  const tierOf: Record<string, number> = {};

  if (teamCount === 0) return { teams, tierOf, unranked };

  const sorted = [...players].sort((a, b) => {
    const ah = a.handicapIndex ?? 0;
    const bh = b.handicapIndex ?? 0;
    if (ah !== bh) return ah - bh;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

  for (let tier = 1; tier <= size; tier++) {
    const start = (tier - 1) * teamCount;
    if (start >= sorted.length) break;
    const slice = sorted.slice(start, start + teamCount);
    const forward = tier % 2 === 1;
    slice.forEach((player, idx) => {
      const teamIdx = forward ? idx : teamCount - 1 - idx;
      teams[teamIdx].memberIds.push(player.id);
      tierOf[player.id] = tier;
    });
  }

  return { teams, tierOf, unranked };
}

/** See `snakeDraft`. Produces "Team 1", "Team 2", … — SEPARATE-mode scoring teams. */
export function draftTiers(players: DraftPlayer[], teamSize: number): DraftResult {
  return snakeDraft(players, teamSize, 'Team');
}

/**
 * See `snakeDraft`. Produces "Group 1", "Group 2", … — the SAME-mode "re-seat the whole field
 * into tiered foursomes from handicap" action. A console turns this into `reseat_groups()`'s
 * `p_groups` the same way it turns `draftTiers` into `apply_scoring_teams()`'s `p_teams`:
 * `team_id: null` for every drafted group (they are all new) and `entrant_ids: memberIds`.
 */
export function draftGroups(players: DraftPlayer[], teamSize: number): DraftResult {
  return snakeDraft(players, teamSize, 'Group');
}

// ── Scoring ──────────────────────────────────────────────────────────────────

export type ScoringTeamMember = {
  id: string;
  name: string;
  scoringTeamId: string | null;
  tier: number | null;
  courseHandicap: number | null;
  withdrawn?: boolean;
  scores: Array<{ holeIdx: number; gross: number }>;
};

export type ScoringTeamBoardInput = {
  rule: TeamRule;
  basis: TeamBasis;
  /** Par per played hole, index-aligned. */
  holePars: number[];
  /** Needed only for `basis: 'net'`. Absent or the wrong length falls back to gross for
   *  every hole, the same "never a confidently wrong number" rule leaderboard.ts follows. */
  holeStrokeIndexes?: number[] | null;
  teams: Array<{ id: string; name: string; seq: number; captainName?: string | null }>;
  entrants: ScoringTeamMember[];
};

export type ScoringTeamRow = {
  id: string;
  name: string;
  seq: number;
  /** The captain's display name, or null if none is set yet — see supabase/migrations/
   *  0023_team_captains.sql. Name only; a captain's contact info is staff-only and never
   *  reaches this board. */
  captainName: string | null;
  /** Holes that counted toward this team's score — see the rule-specific threshold below. */
  thru: number;
  total: number;
  toPar: number;
  /** One entry per played hole: the team's counted score on it, or null if it did not count. */
  holes: Array<number | null>;
  members: Array<{ id: string; name: string; tier: number | null }>;
  /** Shared rank, 1-based; null for a team with thru 0 — nothing to rank yet. */
  position: number | null;
};

/**
 * Standings for tiered scoring teams — the SAME shape of problem `leaderboard()` in
 * src/sync/leaderboard.ts solves for walking groups, one level up: a team here is made of
 * entrants who may be on different cards and different tee times, so this reads each
 * member's own per-hole gross rather than one shared team score.
 *
 * A hole only counts (`thru`) once enough members have a score on it for the rule to mean
 * something:
 *   best1      — any one member (need a low ball to compare at all)
 *   best2      — any two members (need two to take the low pair)
 *   aggregate  — every member who has not withdrawn (a team total with a member missing
 *                is not the team's total)
 *
 * A withdrawn member's own scores, where they exist, still count toward best1/best2/
 * aggregate's sum on a hole they actually played — golf does not erase a walked-in player's
 * holes, only their claim to a placing (see leaderboard.ts's own withdrawn handling). They
 * are simply not required for aggregate's "everybody's in" threshold.
 */
export function scoringTeamBoard(input: ScoringTeamBoardInput): ScoringTeamRow[] {
  const { rule, basis, holePars, teams, entrants } = input;
  const holeCount = holePars.length;
  const si =
    Array.isArray(input.holeStrokeIndexes) && input.holeStrokeIndexes.length === holeCount
      ? input.holeStrokeIndexes
      : null;

  const byTeam = new Map<string, ScoringTeamMember[]>();
  for (const e of entrants) {
    if (!e.scoringTeamId) continue;
    const list = byTeam.get(e.scoringTeamId) ?? [];
    list.push(e);
    byTeam.set(e.scoringTeamId, list);
  }

  const rows: ScoringTeamRow[] = teams.map((team) => {
    const members = byTeam.get(team.id) ?? [];
    const strokesByMember = new Map(
      members.map((m) => [
        m.id,
        basis === 'net' && si ? allocateStrokes(m.courseHandicap ?? 0, si) : ([] as number[]),
      ])
    );
    const scoreAtByMember = new Map(
      members.map((m) => [m.id, new Map(m.scores.map((s) => [s.holeIdx, s.gross]))])
    );

    let thru = 0;
    let total = 0;
    let toPar = 0;
    const holes: Array<number | null> = [];

    for (let h = 0; h < holeCount; h++) {
      const available: number[] = [];
      for (const m of members) {
        const gross = scoreAtByMember.get(m.id)?.get(h);
        if (gross === undefined) continue;
        const strokes = strokesByMember.get(m.id)?.[h] ?? 0;
        available.push(basis === 'net' ? gross - strokes : gross);
      }

      let used: number[] | null = null;
      if (rule === 'best1') {
        if (available.length >= 1) used = [Math.min(...available)];
      } else if (rule === 'best2') {
        if (available.length >= 2) {
          used = [...available].sort((a, b) => a - b).slice(0, 2);
        }
      } else {
        const active = members.filter((m) => m.withdrawn !== true);
        const allActiveScored = active.length > 0 && active.every((m) => scoreAtByMember.get(m.id)?.has(h));
        if (allActiveScored) used = available;
      }

      if (used && used.length > 0) {
        const sum = used.reduce((s, v) => s + v, 0);
        const par = holePars[h] ?? 4;
        thru += 1;
        total += sum;
        toPar += sum - par * used.length;
        holes.push(sum);
      } else {
        holes.push(null);
      }
    }

    return {
      id: team.id,
      name: team.name,
      seq: team.seq,
      captainName: team.captainName?.trim() || null,
      thru,
      total,
      toPar,
      holes,
      members: [...members]
        .sort((a, b) => (a.tier ?? 99) - (b.tier ?? 99) || a.name.localeCompare(b.name))
        .map((m) => ({ id: m.id, name: m.name, tier: m.tier ?? null })),
      position: null,
    };
  });

  const rankable = rows.filter((r) => r.thru > 0).sort((a, b) => a.toPar - b.toPar || b.thru - a.thru);
  const unranked = rows.filter((r) => r.thru === 0);

  let i = 0;
  while (i < rankable.length) {
    let j = i;
    while (j + 1 < rankable.length && rankable[j + 1].toPar === rankable[i].toPar) j++;
    const rank = i + 1;
    for (let k = i; k <= j; k++) rankable[k].position = rank;
    i = j + 1;
  }

  return [...rankable, ...unranked];
}

// ── Team cash board (SEPARATE mode only) ────────────────────────────────────────

export type TeamCashMember = { id: string; name: string; tier: number | null; moneyCents: number };

export type TeamCashRow = {
  id: string;
  name: string;
  seq: number;
  /** Σ non-withdrawn members' `moneyCents`. The figure the board is ranked on. */
  cashCents: number;
  members: TeamCashMember[];
  /** Shared rank, 1-based; null for a team with no members — nothing to rank yet. */
  position: number | null;
};

export type ScoringTeamCashBoardInput = {
  teams: Array<{ id: string; name: string; seq: number }>;
  entrants: Array<{
    id: string;
    name: string;
    scoringTeamId: string | null;
    tier: number | null;
    /** Net side-game money from THIS entrant's own foursome — see this file's header on why
     *  a scoring team's cash cannot be computed here: its members are scattered across
     *  different cards and different games, so the caller (src/sync/leaderboard.ts) folds
     *  each member's own group total before this ever sees it. */
    moneyCents: number;
    withdrawn?: boolean;
  }>;
};

/**
 * How much cash a SEPARATE-mode scoring team has taken off the course between its members,
 * summed from each member's own foursome — Patrick's "teams can have leaderboards for most
 * cash accumulated from 4sums as a team" metric (2026-09-14). Meaningless in SAME mode, where
 * a team IS a foursome and its own side games already net to zero inside it; see
 * `scoringTeamCashRowsFromPayload` for that guard.
 *
 * WITHDRAWN IS EXCLUDED FROM THE SUM, same convention `leaderboard()`'s `placeRows` and
 * `scoringTeamBoard`'s ranking already use for a player who left mid-round — golf does not
 * hand a placing (here, a cash ranking) to someone who did not see the event through. Their
 * own figure still appears in `members` so a host can see where it came from; it is only left
 * out of the team's own `cashCents`.
 *
 * Ranking is a plain descending sort on `cashCents` — there is no thru-count or to-par
 * equivalent here, a dollar is comparable to another dollar regardless of how it was made.
 * Shares a position on a tie, same as every other board in this file. A team with no members
 * (nobody has drafted onto it yet) is left UNRANKED rather than tied for first with $0.
 */
export function scoringTeamCashBoard(input: ScoringTeamCashBoardInput): TeamCashRow[] {
  const { teams, entrants } = input;

  const byTeam = new Map<string, ScoringTeamCashBoardInput['entrants']>();
  for (const e of entrants) {
    if (!e.scoringTeamId) continue;
    const list = byTeam.get(e.scoringTeamId) ?? [];
    list.push(e);
    byTeam.set(e.scoringTeamId, list);
  }

  const rows: TeamCashRow[] = teams.map((team) => {
    const members = byTeam.get(team.id) ?? [];
    const cashCents = members
      .filter((m) => m.withdrawn !== true)
      .reduce((sum, m) => sum + m.moneyCents, 0);

    return {
      id: team.id,
      name: team.name,
      seq: team.seq,
      cashCents,
      members: [...members]
        .sort((a, b) => (a.tier ?? 99) - (b.tier ?? 99) || a.name.localeCompare(b.name))
        .map((m) => ({ id: m.id, name: m.name, tier: m.tier ?? null, moneyCents: m.moneyCents })),
      position: null,
    };
  });

  const rankable = rows.filter((r) => r.members.length > 0).sort((a, b) => b.cashCents - a.cashCents);
  const unranked = rows.filter((r) => r.members.length === 0);

  let i = 0;
  while (i < rankable.length) {
    let j = i;
    while (j + 1 < rankable.length && rankable[j + 1].cashCents === rankable[i].cashCents) j++;
    const rank = i + 1;
    for (let k = i; k <= j; k++) rankable[k].position = rank;
    i = j + 1;
  }

  return [...rankable, ...unranked];
}
